// petStats — derive fighter stats + HP/Mana from kid_pet_state.
//
// As of migration 0264 stats are TIME-WINDOWED. Eating a balanced
// meal buffs stats for 3 hours, then they fade back to baseline.
// The active count comes from kid_card_spends rows in the last 180
// minutes; `useKidPetState` exposes that as `activeStats`. Stats
// are 1:1 with active spends — eat a Build, get +1 ATK for 3h.
//
// Lifetime counters (`spent_total_X`) still drive XP / level / Mana
// max bonus; they aren't surfaced as stat values any more.
//
// Mapping (post-0264):
//   build → ATK         physical attack       (protein)
//   boost → AGI         agility / dodge       (grains/energy)
//   super → S.ATK       special attack        (veggies)
//   spark → S.DEF       special defense       (spices/treats)
//   dairy → DEF         physical defense      (calcium/dairy)
//   water → (no stat)   refills mana via spend_kid_card
//
// HP / Mana scale with level. Defaults match the migration 0247
// constants — if you change them here, change them in the SQL too
// or mana refill will cap at the wrong ceiling on the server.

export const CARDS_PER_STAT  = 5;
export const ACTIVE_WINDOW_MINUTES = 180;

export const BASE_HP        = 50;
export const HP_PER_LEVEL   = 10;
export const BASE_MANA      = 20;
export const MANA_PER_LEVEL = 5;
export const MANA_PER_WATER = 10;
// +1 max mana per 5 lifetime water cards spent. Mirrors the server-
// side constant in migration 0250. Stacks on top of the level term
// so maxMana = BASE + lv*PER_LEVEL + floor(spent_water/5)*BONUS.
export const MAX_MANA_BONUS_PER_5_WATER = 1;

// Order matters — STAT_DEFS is also the render order in the panel.
// Water (new in 0248) intentionally not in this list: it has no
// permanent stat, it just refills mana on spend.
export const STAT_DEFS = [
  { key: "atk",   label: "ATK",   emoji: "💪", tint: "#D96B2B", cardType: "build", longLabel: "Attack" },
  { key: "def",   label: "DEF",   emoji: "🛡️", tint: "#C8A86A", cardType: "dairy", longLabel: "Defense" },
  { key: "agi",   label: "AGI",   emoji: "⚡", tint: "#D4A637", cardType: "boost", longLabel: "Agility" },
  { key: "satk",  label: "S.ATK", emoji: "🥦", tint: "#2F8F83", cardType: "super", longLabel: "Special Attack" },
  { key: "sdef",  label: "S.DEF", emoji: "✨", tint: "#7A4E2D", cardType: "spark", longLabel: "Special Defense" },
];

export const STAT_DEF_BY_CARD_TYPE = Object.fromEntries(
  STAT_DEFS.map((s) => [s.cardType, s]),
);

// Active-window stat derivation — 1:1 with cards spent in the last
// 3 hours. Pass the `activeStats` block from useKidPetState. Each
// card type contributes directly to its stat for the buff window.
//
// `pet` is accepted for forward-compat (in case future stats also
// depend on lifetime totals) but currently unused.
export function statsFromPet(_pet, activeStats) {
  const a = activeStats || EMPTY_ACTIVE;
  return {
    atk:  a.build || 0,
    def:  a.dairy || 0,
    agi:  a.boost || 0,
    satk: a.super || 0,
    sdef: a.spark || 0,
  };
}

const EMPTY_ACTIVE = { boost: 0, spark: 0, build: 0, super: 0, dairy: 0 };

// When does the earliest active spend age out → that's when the
// stat block loses one tick. Returns ms until decay, or null if no
// buff is active.
export function msUntilNextDecay(earliestAt) {
  if (!earliestAt) return null;
  const earliestMs = new Date(earliestAt).getTime();
  if (!Number.isFinite(earliestMs)) return null;
  const expiresAt = earliestMs + ACTIVE_WINDOW_MINUTES * 60 * 1000;
  return Math.max(0, expiresAt - Date.now());
}

// Short human label like "2h left" / "45m left" / "soon". For the
// decay-countdown chip under the stat tiles.
export function decayLabel(earliestAt) {
  const ms = msUntilNextDecay(earliestAt);
  if (ms == null) return null;
  const min = Math.round(ms / 60000);
  if (min <= 1) return "fading";
  if (min < 60) return `${min}m left`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h left` : `${h}h ${m}m left`;
}

// HP block. current_hp is stored; max scales with level. Defaults
// keep a fresh pet at full HP.
export function hpFromPet(pet) {
  const level = Number.isFinite(pet?.level) ? pet.level : 1;
  const max = BASE_HP + level * HP_PER_LEVEL;
  const cur = Number.isFinite(pet?.current_hp) ? Math.max(0, Math.min(max, pet.current_hp)) : max;
  return { current: cur, max };
}

// Mana block. Same shape as HP. Max grows with both level AND
// lifetime water spend (+1 per 5 water — migration 0250). Water
// card spends refill current up to max via the server-side
// spend_kid_card RPC.
export function manaFromPet(pet) {
  const level = Number.isFinite(pet?.level) ? pet.level : 1;
  const water = Number.isFinite(pet?.spent_total_water) ? pet.spent_total_water : 0;
  const waterBonus = Math.floor(Math.max(0, water) / CARDS_PER_STAT) * MAX_MANA_BONUS_PER_5_WATER;
  const max = BASE_MANA + level * MANA_PER_LEVEL + waterBonus;
  const cur = Number.isFinite(pet?.current_mana) ? Math.max(0, Math.min(max, pet.current_mana)) : max;
  return { current: cur, max };
}

// Convenience: "+1 ATK" label for the stat-up flash. Pass the
// card type that was spent. Returns null for card types that
// don't map to a visible stat (boost / Water).
export function statLabelForCardType(cardType) {
  return STAT_DEF_BY_CARD_TYPE[cardType]?.label || null;
}
